import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Casino as CasinoIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import LotteryCard from '../components/LotteryCard';
import { getLotteryList } from '../services/lotteryApi';
import Navigation from '../components/Navigation';

/**
 * 我的彩票查询页面
 * 展示用户已下单的彩票列表及状态
 */
function LotteryListPage() {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const tabOptions = [
    { label: '全部', value: 'all' },
    { label: '待开奖', value: 'pending' },
    { label: '已中奖', value: 'won' },
    { label: '未中奖', value: 'lost' }
  ];

  // 加载列表
  const loadList = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const currentPage = isRefresh ? 1 : page;
      const status = tabOptions[activeTab].value;

      const result = await getLotteryList({
        page: currentPage,
        pageSize: 20,
        status
      });

      if (result.success) {
        const newList = result.data.list;
        if (isRefresh) {
          setList(newList);
        } else {
          setList(prev => [...prev, ...newList]);
        }
        setHasMore(result.data.hasMore);
        setTotal(result.data.total);
        setPage(currentPage);
      } else {
        setError('加载失败，请重试');
      }
    } catch (err) {
      console.error('加载彩票列表失败:', err);
      setError('网络请求失败，请稍后重试');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab, page]);

  // 初始化加载
  useEffect(() => {
    setList([]);
    setPage(1);
    loadList(true);
  }, [activeTab]);

  // 切换标签
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchText('');
  };

  // 下拉刷新
  const handleRefresh = () => {
    loadList(true);
  };

  // 加载更多
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
      loadList(false);
    }
  };

  // 搜索过滤
  const filteredList = list.filter(item =>
    item.orderNo.toLowerCase().includes(searchText.toLowerCase()) ||
    item.period.includes(searchText)
  );

  return (
    <Box>
      {/* 系统导航栏 */}
      <Navigation />
      
      {/* 页面内容 */}
      <Box sx={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', backgroundColor: '#F5F5F5' }}>
      {/* 顶部导航 */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ padding: '12px 16px', display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => history.goBack()} size="small" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            我的彩票
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            共 {total} 注
          </Typography>
        </Box>

        {/* Tab标签 */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 40,
              fontSize: 14,
              fontWeight: 500
            }
          }}
        >
          {tabOptions.map((tab, index) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {/* 搜索框 */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="搜索订单号或期号"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#999', mr: 1, fontSize: 20 }} />,
              sx: { borderRadius: 2 }
            }}
          />
        </Box>
      </Box>

      {/* 内容区域 */}
      <Box sx={{ p: 2, pt: 1 }}>
        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 下拉刷新提示 */}
        {refreshing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* 彩票列表 */}
        {filteredList.length > 0 ? (
          <>
            {filteredList.map(order => (
              <LotteryCard key={order.orderNo} order={order} />
            ))}

            {/* 加载更多按钮 */}
            {hasMore && !loading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading}
                  size="small"
                >
                  加载更多
                </Button>
              </Box>
            )}

            {/* 加载中 */}
            {loading && !refreshing && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </>
        ) : (
          /* 空状态 */
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#EEEEEE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}
            >
              <CasinoIcon sx={{ fontSize: 40, color: '#BDBDBD' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#666', mb: 1, fontWeight: 500 }}>
              暂无彩票记录
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              {activeTab === 0
                ? '快去领取您的第一注彩票吧'
                : `暂无${tabOptions[activeTab].label}的彩票`}
            </Typography>
            <Button
              variant="contained"
              onClick={() => history.push('/lottery/pick')}
              sx={{
                backgroundColor: '#E53935',
                '&:hover': { backgroundColor: '#C62828' }
              }}
            >
              去领取彩票
            </Button>
          </Paper>
        )}
      </Box>

      {/* 底部统计 */}
      {total > 0 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#FFFFFF',
            p: 2,
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'space-around'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#E53935' }}>
              {list.filter(l => l.status === 'won').length}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              已中奖
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF9800' }}>
              {list.filter(l => l.status === 'pending').length}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              待开奖
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E88E5' }}>
              {list.reduce((sum, l) => sum + (l.prize || 0), 0) / 100}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              累计奖金(元)
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
    </Box>
  );
}

export default LotteryListPage;
